import { useEffect, useState, useMemo, useCallback } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllMoisture, deleteMoisture, printMoistureCorrection } from "../../services/MoistureCorrectionServices";
import { displayError } from "../../helpers";
import { showMoistureLinkedDcPopup } from "./MoistureCorrectionDcPopup";
import { openPdfResponse } from "../../utils/openPdfInApp";

function MoistureCorrectionTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const handleDcCellClick = useCallback((row) => {
        if ((row.delivery_challan_count || 0) > 0) {
            showMoistureLinkedDcPopup(row, cookies);
        }
    }, [cookies]);

    const columns = useMemo(
        () => [
            { fieldName: "id", headerName: "ROW_ACTIONS", sticky: "right" },
            { fieldName: "plant_name", headerName: 'Plant' },
            { fieldName: "mc_no", headerName: 'MC No.' },
            { fieldName: "mc_date_display", headerName: 'MC Date' },
            { fieldName: "mc_time", headerName: 'MC Time' },
            { fieldName: "status", headerName: 'Status' },
            {
                fieldName: "delivery_challan_label",
                headerName: 'DC',
                getCellStyle: (row) => ({
                    color: (row.delivery_challan_count || 0) > 0 ? '#2563eb' : '#64748b',
                    cursor: (row.delivery_challan_count || 0) > 0 ? 'pointer' : 'default',
                    textDecoration: (row.delivery_challan_count || 0) > 0 ? 'underline' : 'none',
                    fontWeight: 600,
                }),
                onCellClick: handleDcCellClick,
            },
            { fieldName: "moisture_msand", headerName: 'Moi_MSand' },
            { fieldName: "moisture_csand", headerName: 'Moi_CSand' },
            { fieldName: "moisture_rsand", headerName: 'Moi_RSand' },
            { fieldName: "moisture_12mm", headerName: 'Moi_12mm' },
            { fieldName: "moisture_20mm", headerName: 'Moi_20mm' },
            { fieldName: "absorption_msand", headerName: 'Abs_MSand' },
            { fieldName: "absorption_csand", headerName: 'Abs_CSand' },
            { fieldName: "absorption_rsand", headerName: 'Abs_RSand' },
            { fieldName: "absorption_12mm", headerName: 'Abs_12mm' },
            { fieldName: "absorption_20mm", headerName: 'Abs_20mm' },
        ],
        [handleDcCellClick],
    );

    const onEditMoisturecorr = (moistureId) => {
       navigate(`/MoistureCorrectionEdit/${moistureId}/`);
    };

    const onDeleteMoisturecorr  = (moistureId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                Swal.fire('Not Deleted', '', 'info');
                return;
            }
            setIsLoading(true);
            deleteMoisture(cookies, moistureId)
                .then(() => {
                    Swal.fire('Deleted Successfully!', '', 'success');
                    setRefreshKey((oldKey) => oldKey + 1);
                })
                .catch((err) => {
                    setIsLoading(false);
                    displayError(err.response?.data ?? err.message, 'Delete Failed');
                });
        });
    };

    const onPrintMoistureCorrection = (moistureId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printMoistureCorrection(cookies, moistureId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Moisture Correction Report',
                    downloadFileName: `moisture_correction_${moistureId}.pdf`,
                });
            })
            .catch(async (error) => {
                let message = error?.response?.data || error;
                if (error?.response?.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const parsed = JSON.parse(text);
                        message = parsed.detail || parsed.message || parsed || text;
                    } catch {
                        message = error.message || 'Print failed';
                    }
                }
                displayError(message, 'Print Failed');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter(col => col.headerName !== 'ROW_ACTIONS').map(c => c.headerName),
            data.records.map(r => ({
                plant_name: r.plant_name,
                mc_no: r.mc_no,
                mc_date_display: r.mc_date_display,
                mc_time: r.mc_time,
                status: r.status,
                delivery_challan_label: r.delivery_challan_label,
                moisture_msand: r.moisture_msand,
                moisture_csand: r.moisture_csand,
                moisture_rsand: r.moisture_rsand,
                moisture_12mm: r.moisture_12mm,
                moisture_20mm: r.moisture_20mm,
                absorption_msand: r.absorption_msand,
                absorption_csand: r.absorption_csand,
                absorption_rsand: r.absorption_rsand,
                absorption_12mm: r.absorption_12mm,
                absorption_20mm: r.absorption_20mm,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Moisture Correction',
            columns.filter(col => col.headerName !== 'ROW_ACTIONS').map(col => ({
                header: col.headerName,
                dataKey: col.fieldName,
            })),
            data.records,
            'Moisture.pdf'
        );
    };

    useEffect(() => {
        setIsLoading(true);
        getAllMoisture(cookies)
        .then((moistureList) => {
            const rows = moistureList?.moisture_correction_list || [];
            const tableData = rows.map((mc) => {
                const deliveryChallans = mc.delivery_challans || [];
                const deliveryChallanCount = Number(
                    mc.delivery_challan_count ?? deliveryChallans.length ?? 0
                );
                return {
                    id: mc.id,
                    plant_name: mc.plant_alias || mc.plant_name || '',
                    mc_no: mc.mc_no ?? '',
                    mc_date_display: mc.mc_date_display || mc.mc_date || '',
                    mc_time: mc.mc_time ? String(mc.mc_time).slice(0, 8) : '',
                    status: mc.status || (mc.is_active === false ? 'Inactive' : 'Active'),
                    delivery_challans: deliveryChallans,
                    delivery_challan_count: deliveryChallanCount,
                    delivery_challan_label: deliveryChallanCount > 0 ? String(deliveryChallanCount) : '-',
                    moisture_msand: parseFloat(mc.moisture?.msand ?? 0),
                    moisture_csand: parseFloat(mc.moisture?.csand ?? 0),
                    moisture_rsand: parseFloat(mc.moisture?.rsand ?? 0),
                    moisture_12mm: parseFloat(mc.moisture?.['12mm'] ?? 0),
                    moisture_20mm: parseFloat(mc.moisture?.['20mm'] ?? 0),
                    absorption_msand: parseFloat(mc.absorption?.msand ?? 0),
                    absorption_csand: parseFloat(mc.absorption?.csand ?? 0),
                    absorption_rsand: parseFloat(mc.absorption?.rsand ?? 0),
                    absorption_12mm: parseFloat(mc.absorption?.['12mm'] ?? 0),
                    absorption_20mm: parseFloat(mc.absorption?.['20mm'] ?? 0),
                };
            });
            setData({
                total_pages: moistureList.total_pages ?? 0,
                records: tableData,
            });
        })
        .catch((err) => {
            setData({ total_pages: 0, records: [] });
            displayError(
                err.response?.data ?? err.message,
                'Failed to load moisture correction list'
            );
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [cookies, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader
                module="QUALITY CONTROL"
                title="List of Moisture Correction"
                subtitle="View MC documents by plant. Click DC count to see linked delivery challans."
                formLink="/MoistureCorrection"
                addNewLabel="New Moisture Correction"
                homeLink="/Home"
            />
            <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Moisture Correction'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditMoisturecorr}
                            onRowDelete={onDeleteMoisturecorr}
                            onRowPrint={onPrintMoistureCorrection}
                            hidePrint={false}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                        />
                </div>
                </div>
            </div>
        </div>
    );
}

export default MoistureCorrectionTable;
